import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import Badge from '@/components/common/Badge';
import useAppStore from '@/store/appStore';
import api from '@/utils/api';
import { Shield, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

const defaultRedirectUri = () => `${window.location.origin}/api/oauth2/callback`;

export default function OAuth2SettingsSection() {
  const { addNotification } = useAppStore();
  const queryClient = useQueryClient();
  const [showSecrets, setShowSecrets] = useState({
    google: false,
    microsoft: false
  });
  const [editingProvider, setEditingProvider] = useState(null);
  const [formData, setFormData] = useState({});

  // Fetch OAuth2 settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings', 'oauth2'],
    queryFn: async () => {
      // Note: api interceptor already returns response.data
      const data = await api.get('/settings/oauth2', {
        headers: { 'Cache-Control': 'no-cache' }
      });
      return data;
    },
    staleTime: 0
  });

  // Update OAuth2 settings mutation
  const updateMutation = useMutation({
    mutationFn: async ({ provider, data }) => {
      const response = await api.put(`/settings/oauth2/${provider}`, data);
      return response.data;
    },
    onSuccess: async (data, variables) => {
      addNotification({
        type: 'success',
        title: 'OAuth2 Settings Updated',
        message: `Settings for ${variables.provider} updated successfully`
      });
      // Force immediate refetch instead of just invalidating
      await Promise.all([
        queryClient.refetchQueries(['settings', 'oauth2']),
        queryClient.refetchQueries(['oauth2', 'providers'])
      ]);
      setEditingProvider(null);
      setFormData({});
    },
    onError: (error, variables) => {
      addNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.response?.data?.error || error.message
      });
    }
  });

  // Clear OAuth2 settings mutation
  const clearMutation = useMutation({
    mutationFn: async (provider) => {
      const response = await api.delete(`/settings/oauth2/${provider}`);
      return response.data;
    },
    onSuccess: (data, provider) => {
      addNotification({
        type: 'success',
        title: 'OAuth2 Settings Cleared',
        message: `Settings for ${provider} cleared successfully`
      });
      queryClient.invalidateQueries(['settings', 'oauth2']);
      queryClient.invalidateQueries(['oauth2', 'providers']);
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Clear Failed',
        message: error.response?.data?.error || error.message
      });
    }
  });

  const handleEdit = (provider) => {
    const providerSettings = settings?.[provider] || {};
    setEditingProvider(provider);
    setFormData({
      clientId: providerSettings.clientId || '',
      clientSecret: '',
      redirectUri: providerSettings.redirectUri || defaultRedirectUri()
    });
  };

  const handleCancel = () => {
    setEditingProvider(null);
    setFormData({});
  };

  const handleSave = (provider) => {
    const updates = {
      clientId: formData.clientId,
      redirectUri: formData.redirectUri
    };

    // Only include secret if it was changed
    if (formData.clientSecret) {
      updates.clientSecret = formData.clientSecret;
    }

    updateMutation.mutate({ provider, data: updates });
  };

  const handleClear = (provider) => {
    if (confirm(`Are you sure you want to clear OAuth2 settings for ${provider}? Users will need to use basic authentication until reconfigured.`)) {
      clearMutation.mutate(provider);
    }
  };

  const toggleShowSecret = (provider) => {
    setShowSecrets(prev => ({ ...prev, [provider]: !prev[provider] }));
  };

  const renderProviderConfig = (provider, providerName) => {
    const providerSettings = settings?.[provider] || {};
    const isConfigured = providerSettings.configured;
    const isEditing = editingProvider === provider;

    return (
      <div key={provider} className="p-4 border rounded-lg space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-lg font-semibold">{providerName}</div>
            <Badge variant={isConfigured ? 'success' : 'secondary'}>
              {isConfigured ? (
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Configured
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <XCircle className="h-3 w-3" />
                  Not Configured
                </span>
              )}
            </Badge>
          </div>
          {!isEditing && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleEdit(provider)}
              >
                {isConfigured ? 'Edit' : 'Configure'}
              </Button>
              {isConfigured && (
                <Button
                  size="sm"
                  variant="destructive-ghost"
                  onClick={() => handleClear(provider)}
                  loading={clearMutation.isPending && clearMutation.variables === provider}
                >
                  Clear
                </Button>
              )}
            </div>
          )}
        </div>

        {!isEditing && isConfigured && (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-1 border-b">
              <span className="text-muted-foreground">Client ID:</span>
              <span className="font-mono text-xs">{providerSettings.clientId}</span>
            </div>
            <div className="flex justify-between py-1 border-b">
              <span className="text-muted-foreground">Client Secret:</span>
              <span className="font-mono text-xs text-muted-foreground">***masked***</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-muted-foreground">Redirect URI:</span>
              <span className="font-mono text-xs">{providerSettings.redirectUri}</span>
            </div>
          </div>
        )}

        {isEditing && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Client ID</label>
              <Input
                type="text"
                value={formData.clientId || ''}
                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                placeholder="Enter OAuth2 Client ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Client Secret</label>
              <div className="flex gap-2">
                <Input
                  type={showSecrets[provider] ? 'text' : 'password'}
                  value={formData.clientSecret || ''}
                  onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
                  placeholder={isConfigured ? "Leave empty to keep current" : "Enter OAuth2 Client Secret"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => toggleShowSecret(provider)}
                >
                  {showSecrets[provider] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Redirect URI</label>
              <Input
                type="text"
                value={formData.redirectUri || ''}
                onChange={(e) => setFormData({ ...formData, redirectUri: e.target.value })}
                placeholder={defaultRedirectUri()}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Add this URL to your OAuth2 app's authorized redirect URIs
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => handleSave(provider)}
                loading={updateMutation.isPending && updateMutation.variables?.provider === provider}
              >
                Save
              </Button>
              <Button
                variant="ghost"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          OAuth2 Configuration
        </CardTitle>
        <CardDescription>
          Configure OAuth2 authentication for email providers. Users can sign in with their browser instead of app passwords.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-4">
            {renderProviderConfig('google', 'Google (Gmail)')}
            {renderProviderConfig('microsoft', 'Microsoft (Outlook/Hotmail)')}

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="font-semibold mb-2 text-blue-900 dark:text-blue-100">
                Setup Instructions
              </h4>
              <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                <p><strong>Google:</strong></p>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                  <li>Create OAuth 2.0 credentials (Web Application)</li>
                  <li>Add redirect URI (shown above)</li>
                  <li>Copy Client ID and Secret here</li>
                </ol>
                <p className="mt-3"><strong>Microsoft:</strong></p>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="underline">Azure Portal</a></li>
                  <li>Register an application</li>
                  <li>Add API permissions: IMAP.AccessAsUser.All, offline_access</li>
                  <li>Add redirect URI (shown above)</li>
                  <li>Create client secret and copy here</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
